import { ReactNode } from "react";

import { UserDto } from "../../DTOs/User/UserDto";
import { AreaKey } from "../../utilities/helpers/pageVocabulary.generated";
import { hasArea, hasTile } from "../../utilities/helpers/roleUtilities";
import { CardSize, PlacedCard } from "./dashboardLayout";
import { DrillBlastTodayCard } from "./DashboardCards/DrillBlastTodayCard";
import { ExplosivesStockCard } from "./DashboardCards/ExplosivesStockCard";
import { HourlyMetricsCard } from "./DashboardCards/HourlyMetricsCard";
import { ImportChecksCard } from "./DashboardCards/ImportChecksCard";
import { LazyMachinePositionsCard } from "./DashboardCards/LazyMachinePositionsCard";
import { MaterialMovementCard } from "./DashboardCards/MaterialMovementCard";
import { PlantStatusCard } from "./DashboardCards/PlantStatusCard";
import { TargetsCard } from "./DashboardCards/TargetsCard";

export interface DashboardCardDefinition {
  key: string;
  title: string;
  linkTo: string;
  linkTooltip: string;
  linkArea: AreaKey;
  defaultSize: CardSize;
  isVisible?: (user: UserDto | null) => boolean;
  content: ReactNode;
}

export const DASHBOARD_CARDS: DashboardCardDefinition[] = [
  {
    key: "material-movement",
    title: "Material Movement",
    linkTo: "/production/show",
    linkTooltip: "Open Production",
    linkArea: "Production",
    defaultSize: { w: 6, h: "medium" },
    content: <MaterialMovementCard />,
  },
  {
    key: "plant-status",
    title: "Plant Status",
    linkTo: "/stoppages",
    linkTooltip: "Open Stoppages",
    linkArea: "Stoppages",
    defaultSize: { w: 6, h: "medium" },
    content: <PlantStatusCard />,
  },
  {
    key: "targets",
    title: "Targets",
    linkTo: "/admin/references/target-refs/targets",
    linkTooltip: "Open Targets",
    linkArea: "Targets",
    defaultSize: { w: 6, h: "medium" },
    content: <TargetsCard />,
  },
  {
    key: "import-checks",
    title: "Import Checks",
    linkTo: "/operations",
    linkTooltip: "Open Operations",
    linkArea: "Operations",
    defaultSize: { w: 6, h: "medium" },
    content: <ImportChecksCard />,
  },
  {
    key: "machine-positions",
    title: "Machine Positions",
    linkTo: "/telemetry/machine-positions",
    linkTooltip: "Open Machine Positions",
    linkArea: "MachinePositions",
    defaultSize: { w: 6, h: "medium" },
    isVisible: user => hasArea(user, "MachinePositions"),
    content: <LazyMachinePositionsCard />,
  },
  {
    key: "drill-blast-today",
    title: "Drill & Blast Today",
    linkTo: "/db/bh-drilling-list",
    linkTooltip: "Open Blast Hole Drilling List",
    linkArea: "BlastHoleDrillingList",
    defaultSize: { w: 6, h: "short" },
    content: <DrillBlastTodayCard />,
  },
  {
    key: "explosives-stock",
    title: "Explosives Stock",
    linkTo: "/db/explosives",
    linkTooltip: "Open Explosives Store",
    linkArea: "ExplosivesStore",
    defaultSize: { w: 6, h: "short" },
    isVisible: user => hasArea(user, "ExplosivesStore"),
    content: <ExplosivesStockCard />,
  },
  {
    key: "hourly-metrics",
    title: "Hourly Metrics",
    linkTo: "/reports/daily",
    linkTooltip: "Open Daily Report",
    linkArea: "DailyReport",
    defaultSize: { w: 12, h: "medium" },
    content: <HourlyMetricsCard />,
  },
];

export type DashboardPlacement = PlacedCard<DashboardCardDefinition>;

export const availableCards = (user: UserDto | null): DashboardCardDefinition[] =>
  DASHBOARD_CARDS.filter(card => hasTile(user, card.key) && (card.isVisible?.(user) ?? true));
